import { useState, useEffect } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { api } from '../api';

const MESES = ['','Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const COLORES = ['#4cc9f0','#f72585','#7209b7','#3a0ca3','#4361ee'];

export default function Dashboard() {
  const ahora = new Date();
  const [mes, setMes] = useState(ahora.getMonth() + 1);
  const [anio, setAnio] = useState(ahora.getFullYear());
  const [datos, setDatos] = useState(null);
  const [validaciones, setValidaciones] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => { cargarDatos(); }, [mes, anio]);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const [kpis, vals] = await Promise.all([
        api.get(`/dashboard/kpis?mes=${mes}&anio=${anio}`),
        api.get(`/dashboard/validaciones?mes=${mes}&anio=${anio}`)
      ]);
      setDatos(kpis.error ? null : kpis);
      setValidaciones(vals);
    } catch (e) {
      setDatos(null);
    } finally {
      setCargando(false);
    }
  };

  const mesAnterior = () => {
    if (mes === 3 && anio === 2026) return;
    if (mes === 1) { setMes(12); setAnio(anio - 1); }
    else setMes(mes - 1);
  };

  const mesSiguiente = () => {
    const esHoy = mes === ahora.getMonth() + 1 && anio === ahora.getFullYear();
    if (esHoy) return;
    if (mes === 12) { setMes(1); setAnio(anio + 1); }
    else setMes(mes + 1);
  };

  const esMesActual = mes === ahora.getMonth() + 1 && anio === ahora.getFullYear();
  const esMesMinimo = mes === 3 && anio === 2026;

  return (
    <div>
      <div style={s.headerRow}>
        <h2 style={s.titulo}>📊 Dashboard</h2>
        <div style={s.selectorMes}>
          <button style={{...s.flecha, ...(esMesMinimo ? s.flechaDesactivada : {})}} onClick={mesAnterior}>‹</button>
          <span style={s.mesLabel}>{MESES[mes]} {anio}{esMesActual ? ' • Actual' : ''}</span>
          <button style={{...s.flecha, ...(esMesActual ? s.flechaDesactivada : {})}} onClick={mesSiguiente}>›</button>
        </div>
      </div>

      {cargando ? (
        <p style={s.cargando}>Cargando...</p>
      ) : !datos ? (
        <p style={s.error}>No se pudo cargar el dashboard</p>
      ) : (
        <>
          {/* KPIs */}
          <div style={s.kpisRow}>
            <div style={{...s.kpiCard, borderTopColor: '#4cc9f0'}}>
              <div style={s.kpiValor}>{datos.total_mes}</div>
              <div style={s.kpiLabel}>Órdenes del mes</div>
            </div>
            <div style={{...s.kpiCard, borderTopColor: '#f72585'}}>
              <div style={s.kpiValor}>{datos.promedio_km > 0 ? datos.promedio_km.toLocaleString() : '—'}</div>
              <div style={s.kpiLabel}>KM promedio recorridos</div>
            </div>
            <div style={{...s.kpiCard, borderTopColor: '#7209b7'}}>
              <div style={{...s.kpiValor, fontSize: 18}}>{datos.tecnico_mes.nombre}</div>
              <div style={s.kpiLabel}>Técnico del mes 🏆</div>
              {datos.tecnico_mes.ordenes > 0 && <div style={s.kpiBadge}>{datos.tecnico_mes.ordenes} órdenes</div>}
            </div>
            {validaciones && (
              <>
                <div style={{...s.kpiCard, borderTopColor: '#4caf50'}}>
                  <div style={s.kpiValor}>{validaciones.validadas}</div>
                  <div style={s.kpiLabel}>✅ Validadas</div>
                </div>
                <div style={{...s.kpiCard, borderTopColor: '#f44336'}}>
                  <div style={s.kpiValor}>{validaciones.no_validadas}</div>
                  <div style={s.kpiLabel}>❌ No validadas</div>
                </div>
                <div style={{...s.kpiCard, borderTopColor: '#ffc107'}}>
                  <div style={s.kpiValor}>{validaciones.pendientes}</div>
                  <div style={s.kpiLabel}>⏳ Pendientes</div>
                </div>
              </>
            )}
          </div>

          {/* Gráficos */}
          <div style={s.graficasRow}>
            {/* Barras por técnico */}
            {datos.por_tecnico.length > 0 && (
              <div style={s.graficaCard}>
                <h3 style={s.graficaTitulo}>📋 Órdenes por Técnico</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={datos.por_tecnico}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" />
                    <XAxis dataKey="tecnico" stroke="#aaa" fontSize={12} />
                    <YAxis stroke="#aaa" fontSize={12} />
                    <Tooltip contentStyle={{background:'#1e1e2e', border:'none', color:'#fff'}} />
                    <Bar dataKey="total" fill="#4cc9f0" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
                {datos.por_tecnico.map((t, i) => (
                  <div key={i} style={s.detalleRow}>
                    <div style={{...s.detalleColor, background: COLORES[i % COLORES.length]}} />
                    <span style={s.detalleNombre}>{t.tecnico}</span>
                    <div style={s.detalleBarra}>
                      <div style={{...s.detalleBarraFill, width: `${(t.total/datos.por_tecnico[0].total)*100}%`, background: COLORES[i % COLORES.length]}} />
                    </div>
                    <span style={s.detalleTotal}>{t.total}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Pastel por tipo */}
            {datos.por_tipo.length > 0 && (
              <div style={s.graficaCard}>
                <h3 style={s.graficaTitulo}>🚗 Tipo de Vehículo</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={datos.por_tipo} dataKey="total" nameKey="tipo" cx="50%" cy="50%" outerRadius={90} label={({tipo, total}) => `${tipo}: ${total}`}>
                      {datos.por_tipo.map((_, i) => <Cell key={i} fill={COLORES[i % COLORES.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{background:'#1e1e2e', border:'none', color:'#fff'}} />
                  </PieChart>
                </ResponsiveContainer>
                {datos.por_tipo.map((t, i) => (
                  <div key={i} style={s.detalleRow}>
                    <div style={{...s.detalleColor, background: COLORES[i % COLORES.length]}} />
                    <span style={s.detalleNombre}>{t.tipo}</span>
                    <div style={s.detalleBarra}>
                      <div style={{...s.detalleBarraFill, width: `${(t.total/datos.por_tipo[0].total)*100}%`, background: COLORES[i % COLORES.length]}} />
                    </div>
                    <span style={s.detalleTotal}>{t.total}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {datos.total_mes === 0 && (
            <div style={s.sinDatos}>📭 Sin órdenes en {MESES[mes]} {anio}</div>
          )}
        </>
      )}
    </div>
  );
}

const s = {
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  titulo: { color: '#fff', margin: 0 },
  selectorMes: { display: 'flex', alignItems: 'center', gap: 12, background: '#1e1e2e', borderRadius: 10, padding: '8px 16px' },
  flecha: { background: 'none', border: 'none', color: '#4cc9f0', fontSize: 24, cursor: 'pointer', fontWeight: 'bold' },
  flechaDesactivada: { color: '#444', cursor: 'default' },
  mesLabel: { color: '#fff', fontSize: 14, fontWeight: 'bold', minWidth: 140, textAlign: 'center' },
  kpisRow: { display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' },
  kpiCard: { background: '#1e1e2e', borderRadius: 12, padding: 20, borderTop: '3px solid', minWidth: 140, flex: 1 },
  kpiValor: { color: '#fff', fontSize: 28, fontWeight: 'bold', marginBottom: 6 },
  kpiLabel: { color: '#aaa', fontSize: 12 },
  kpiBadge: { color: '#4cc9f0', fontSize: 11, marginTop: 4 },
  graficasRow: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  graficaCard: { background: '#1e1e2e', borderRadius: 12, padding: 20, flex: 1, minWidth: 300 },
  graficaTitulo: { color: '#fff', marginBottom: 16, fontSize: 15 },
  detalleRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 },
  detalleColor: { width: 10, height: 10, borderRadius: 5 },
  detalleNombre: { color: '#fff', fontSize: 13, width: 80 },
  detalleBarra: { flex: 1, background: '#2a2a3e', borderRadius: 4, height: 8 },
  detalleBarraFill: { height: 8, borderRadius: 4 },
  detalleTotal: { color: '#4cc9f0', fontSize: 13, fontWeight: 'bold', width: 24 },
  cargando: { color: '#aaa' },
  error: { color: '#e63946' },
  sinDatos: { color: '#555', textAlign: 'center', marginTop: 60, fontSize: 16 },
};